import * as React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Title3, Button } from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import MarkerGrid from './components/MarkerGrid';
import MarkerForm from './components/MarkerForm';
import { useMarker } from '../../hooks/useMarker';
import { Marker, MarkerCreate } from '../../types/marker';
import { ApiErrorInfo } from '../../types/api';
import ConfirmDialog from '../../components/ConfirmDialog';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
  drawer: {
    // Drawer lateral
  },
});

const MarkerManagement: React.FC = () => {
  const styles = useStyles();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [editingMarker, setEditingMarker] = React.useState<Marker | undefined>();
  const [wasSaving, setWasSaving] = React.useState(false);
  const [currentError, setCurrentError] = React.useState<ApiErrorInfo | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [markerToDelete, setMarkerToDelete] = React.useState<Marker | null>(null);

  const { markers, listLoading, listError, saveLoading, saveError, createMarker, updateMarker, deleteMarker, fetchMarkers } = useMarker();

  // Sincronizar erro atual com saveError, mas permitir limpeza
  React.useEffect(() => {
    if (saveError) {
      setCurrentError(saveError);
    }
  }, [saveError]);

  // Carregar markers ao montar o componente
  React.useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  const handleAdd = () => {
    setEditingMarker(undefined);
    setCurrentError(null); // Limpar erro anterior ao abrir novo cadastro
    setIsDrawerOpen(true);
  };

  const handleEdit = (marker: Marker) => {
    setEditingMarker(marker);
    setCurrentError(null); // Limpar erro anterior ao editar
    setIsDrawerOpen(true);
  };

  const handleDelete = (id: number) => {
    const marker = markers.find((m: Marker) => m.id === id);
    if (marker) {
      setMarkerToDelete(marker);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (markerToDelete) {
      try {
        await deleteMarker(markerToDelete.id);
        // Após exclusão bem-sucedida, atualizar a lista
        fetchMarkers();
      } catch (error) {
        // Erro será tratado pelo estado error do hook
        console.error('Erro ao excluir marker:', error);
      }
    }
    setDeleteDialogOpen(false);
    setMarkerToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setMarkerToDelete(null);
  };

  const handleSaveMarker = async (markerData: MarkerCreate) => {
    setWasSaving(true);
    setCurrentError(null); // Limpar erro anterior
    try {
      if (editingMarker) {
        await updateMarker(editingMarker.id, markerData);
      } else {
        await createMarker(markerData);
      }
      // Sucesso será tratado pelo useEffect abaixo
    } catch (error) {
      // Erro será tratado pelo estado error do hook useMarker
      console.error('Erro ao salvar marker:', error);
      // Não definir setWasSaving(false) aqui - deixar para o useEffect
    }
  };

  // Fechar drawer quando a operação de salvar for concluída
  React.useEffect(() => {
    if (wasSaving && !saveLoading) {
      if (!saveError) {
        // Sucesso: fechar drawer e atualizar lista
        setIsDrawerOpen(false);
        fetchMarkers();
        setWasSaving(false);
        setEditingMarker(undefined);
        setCurrentError(null);
      } else {
        // Houve erro, manter drawer aberto para mostrar erro
        setWasSaving(false);
        // currentError será atualizado pelo useEffect que sincroniza com saveError
      }
    }
  }, [wasSaving, saveLoading, saveError, fetchMarkers]);

  const handleCancel = () => {
    setIsDrawerOpen(false);
    setEditingMarker(undefined);
    setCurrentError(null); // Limpar erro ao cancelar
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Marcadores</Title3>
        <Button
          appearance="primary"
          size="large"
          icon={<Add20Regular />}
          onClick={handleAdd}
          className={styles.addButton}
        >
          Novo Marcador
        </Button>
      </div>

      <div className={styles.content}>
        <MarkerGrid
          markers={markers}
          loading={listLoading}
          error={listError?.message || null}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Marcador"
        message={`Tem certeza que deseja excluir o marcador "${markerToDelete?.descricao}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        intent="danger"
      />

      <Drawer
        open={isDrawerOpen}
        onOpenChange={(open) => {
          if (!open) handleCancel();
        }}
        position="end"
        size="large"
        className={styles.drawer}
      >
        <DrawerHeader>
          <DrawerHeaderTitle>
            {editingMarker ? 'Editar Marcador' : 'Novo Marcador'}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          <MarkerForm
            marker={editingMarker}
            onSave={handleSaveMarker}
            onCancel={handleCancel}
            loading={saveLoading}
            error={currentError}
          />
        </DrawerBody>
      </Drawer>
    </div>
  );
};

export default MarkerManagement;